import React, { useEffect, useState } from 'react';
import { X, Clock, User, MessageSquare, ArrowRight } from 'lucide-react';
import api from './axiosConfig';
import { format } from 'date-fns'; 
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة

const StatusHistoryModal = ({ appName, recordId, onClose }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await api.get(`/status/history/${appName}/${recordId}`);
        setHistory(res.data);
      } catch (err) {
        console.error("Failed to load history");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [appName, recordId]);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[80vh]">
        
        {/* Header */}
        <div className="bg-[#005495] p-4 flex justify-between items-center text-white">
          <div className="flex items-center gap-3">
            <Clock size={20} className="text-blue-200" />
            <div>
              <h3 className="font-black uppercase tracking-tighter text-sm">{t('statusHistory.title')}</h3>
              <p className="text-[10px] text-blue-100 opacity-80">{appName}: {recordId}</p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 p-1 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50 custom-scrollbar">
          {loading ? (
            <div className="flex justify-center p-10 font-bold text-gray-400 animate-pulse">{t('statusHistory.loading')}</div>
          ) : history.length === 0 ? (
            <div className="text-center p-20 text-gray-400 italic">{t('statusHistory.noRecords')}</div>
          ) : (
            <div className="space-y-4">
              {history.map((item, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
                  {/* Decorative Timeline line */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500"></div>
                  
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Status Change */}
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-[10px] font-bold border border-gray-200 uppercase">
                        {item.old_status || t('statusHistory.initial')}
                      </span>
                      <ArrowRight size={14} className={`text-gray-400 ${isRTL ? 'rotate-180' : ''}`} />
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-[10px] font-bold border border-blue-200 uppercase">
                        {item.new_status}
                      </span>
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center gap-4 text-[11px] text-gray-500 font-medium">
                      <div className="flex items-center gap-1.5">
                        <User size={14} className="text-blue-400" />
                        <span className="font-bold text-gray-700">{item.changeby}</span>
                      </div>
                      <div className="flex items-center gap-1.5 border-l pl-4">
                        <Clock size={14} className="text-orange-400" />
                        <span>{format(new Date(item.changedate), 'yyyy-MM-dd HH:mm')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Memo/Reason */}
                  {item.memo && (
                    <div className="mt-3 bg-gray-50 p-2 rounded border-l-2 border-orange-200 flex items-start gap-2 italic text-[11px] text-gray-600">
                      <MessageSquare size={12} className="mt-0.5 text-gray-400 shrink-0" />
                      <span>{item.memo}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-100 p-3 flex justify-end border-t">
          <button 
            onClick={onClose}
            className="px-6 py-2 bg-gray-800 text-white rounded font-bold uppercase text-[10px] hover:bg-black transition-all"
          >
            {t('statusHistory.btnClose')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusHistoryModal;